// API service for appointment management
import { isValidAppointmentDate } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class AppointmentService {
  // Get all appointments
  static async getAllAppointments(token, limit = 50, lastKey = null) {
    try {
      let url = `${API_BASE_URL}/appointments?limit=${limit}`;
      if (lastKey) {
        url += `&lastKey=${encodeURIComponent(JSON.stringify(lastKey))}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  }

  // Get appointment by ID
  static async getAppointmentById(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/appointments/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching appointment:', error);
      throw error;
    }
  }

  // Get appointments by date range
  static async getAppointmentsByDateRange(startDate, endDate, token) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/appointments/date-range?startDate=${startDate}&endDate=${endDate}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching appointments by date range:', error);
      throw error;
    }
  }

  // Get appointments for a specific month
  static async getAppointmentsByMonth(year, month, token) {
    try {
      // Create start and end dates for the month
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      
      return this.getAppointmentsByDateRange(startDate, endDate, token);
    } catch (error) {
      console.error('Error fetching appointments by month:', error);
      throw error;
    }
  }

  // Get appointments for a specific date
  static async getAppointmentsByDate(date, token) {
    // Never query the API with incomplete or out-of-range dates
    if (!isValidAppointmentDate(date)) {
      return [];
    }

    try {
      const response = await fetch(`${API_BASE_URL}/appointments/date/${date}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching appointments by date:', error);
      throw error;
    }
  }

  static async sendWhatsAppReminders(date, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/appointments/reminders/whatsapp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ date }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data.data;
    } catch (error) {
      console.error('Error sending WhatsApp reminders:', error);
      throw error;
    }
  }

  // Create new appointment
  // Pass `{ force: true }` to bypass a 409 conflict (i.e. overbook).
  static async createAppointment(appointmentData, token, options = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}/appointments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...appointmentData, force: !!options.force }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          const error = new Error(data.message || 'Ya existe una cita en el mismo horario');
          error.status = 409;
          error.conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
          throw error;
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data.data;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  }

  // Update appointment
  // Pass `{ force: true }` to bypass a 409 conflict (i.e. overbook).
  static async updateAppointment(id, appointmentData, token, options = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}/appointments/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...appointmentData, force: !!options.force }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          const error = new Error(data.message || 'Updated appointment conflicts with existing appointments');
          error.status = 409;
          error.conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
          throw error;
        }
        if (response.status === 404) {
          throw new Error('Appointment not found');
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data.data;
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  }

  // Delete appointment
  static async deleteAppointment(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/appointments/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting appointment:', error);
      throw error;
    }
  }

  // Search appointments
  static async searchAppointments(query, token) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/appointments/search?query=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error searching appointments:', error);
      throw error;
    }
  }

  // Get appointment statistics
  static async getAppointmentStats(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/appointments/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching appointment statistics:', error);
      throw error;
    }
  }

  // Get appointments by patient ID
  static async getAppointmentsByPatient(patientId, token) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/appointments/patient/${patientId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching appointments by patient:', error);
      throw error;
    }
  }

  // Update appointment status
  static async updateAppointmentStatus(id, status, token, notes = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/appointments/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status,
          notes 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data.data;
    } catch (error) {
      console.error('Error updating appointment status:', error);
      throw error;
    }
  }
}

export default AppointmentService;
